<template>
  <div class="product-images">
    <!-- Desktop -->
    <div class="d-none d-md-block">
      <div class="main-image-container">
        <img 
          @click="showImage(selectedImage.url)" 
          :src="selectedImage.url" 
          :class="['main-image', slideDirection]" 
          @animationend="resetSlide" />
      </div>
    </div>

    <!-- Mobile -->
    <div class="d-md-none">
      <div
        class="main-image-container"
        @touchstart="startTouch"
        @touchmove="moveTouch"
        @touchend="endTouch">
        <img 
          :src="selectedImage.url" 
          :class="['main-image', slideDirection]" 
          @animationend="resetSlide" />
      </div>
    </div>

    <!-- Thumbnails -->
    <div v-if="product.images.length > 1" class="thumbnails mt-2 px-2 mx-auto">
      <div
        v-for="(image, index) in product.images"
        :key="index"
        class="thumbnail-container d-flex justify-content-center align-items-center"
        @click="selectImage(image)"
        :class="{ 'active': image.url === selectedImage.url, 'last-container': isLastImage(index) }">
        <img :src="image.url" class="thumbnail" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['product'],
  data() {
    return {
      selectedImage: null,
      currentIndex: 0,
      startX: 0,
      moveX: 0,
      isSwiping: false,
      swipeThreshold: 40,
      slideDirection: null,
    };
  },

  created() {
    this.selectedImage = this.product.images[0];
    this.currentIndex = 0;
    // Add global keydown listener here
    window.addEventListener('keydown', this.handleKeydown);
  },

  beforeDestroy() {
    // Clean up the global keydown listener when the component is destroyed
    window.removeEventListener('keydown', this.handleKeydown);
  },

  methods: {
    isLastImage(index) {
      return (index + 1) % 4 === 0;
    },

    showImage(url) {
      this.currentIndex = this.product.images.findIndex(image => image.url === url);
      this.openModal();
    },

    openModal() {
      Swal.fire({
        html: `
          <div class="swal-image-container" tabindex="0">
            <img id="swal-image" src="${this.selectedImage.url}" class="swal-image" />
            <button id="prev-button" class="prev-button"><i class="fa-solid fa-arrow-left"></i></button>
            <button id="next-button" class="next-button"><i class="fas fa-arrow-circle-right"></i></button>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        showClass: { popup: null },
        hideClass: { popup: null },
        backdrop: true,
        width: '100vw',
        padding: '0',
        customClass: {
          popup: 'swal-fullscreen',
        },
        didOpen: () => {
          const modal = document.querySelector('.swal-image-container');
          modal.focus();
          modal.addEventListener('keydown', this.handleKeydown);
          document.getElementById('prev-button').addEventListener('click', this.prevImage);
          document.getElementById('next-button').addEventListener('click', this.nextImage);
        },
        willClose: () => {
          const modal = document.querySelector('.swal-image-container');
          modal.removeEventListener('keydown', this.handleKeydown);
          document.getElementById('prev-button').removeEventListener('click', this.prevImage);
          document.getElementById('next-button').removeEventListener('click', this.nextImage);
        },
      });
    },

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        this.prevImage();
      } else if (event.key === 'ArrowRight') {
        this.nextImage();
      }
    },

    startTouch(event) {
      this.startX = event.touches[0].clientX;
      this.isSwiping = false; // Reset swiping state
    },

    moveTouch(event) {
      this.moveX = event.touches[0].clientX;

      // Calculate swipe distance
      const swipeDistance = this.startX - this.moveX;

      // If the swipe distance is beyond the threshold, mark it as a swipe
      if (Math.abs(swipeDistance) > this.swipeThreshold) {
        this.isSwiping = true;
      }
    },

    endTouch() {
      const swipeDistance = this.startX - this.moveX;

      // If a swipe occurred (distance is greater than threshold)
      if (this.isSwiping) {
        if (swipeDistance > this.swipeThreshold) {
          // Swipe left (next image)
          this.slideDirection = 'slide-left';
          this.nextImage();
        } else if (swipeDistance < -this.swipeThreshold) {
          // Swipe right (previous image)
          this.slideDirection = 'slide-right';
          this.prevImage();
        }
      }

      // Reset touch coordinates and swiping state
      this.startX = 0;
      this.moveX = 0;
      this.isSwiping = false;
    },

    selectImage(image) {
      if (this.selectedImage.url !== image.url) {
        this.slideDirection = ''; // Reset the slide before applying a new one
        this.selectedImage = image;
        this.currentIndex = this.product.images.indexOf(image);
      }
    },

    prevImage() {
      // Move to the previous image; loop to the last if at the first image
      this.currentIndex = (this.currentIndex - 1 + this.product.images.length) % this.product.images.length;
      this.updateImage();
    },

    nextImage() {
      // Move to the next image; loop to the first if at the last image
      this.currentIndex = (this.currentIndex + 1) % this.product.images.length;
      this.updateImage();
    },

    updateImage() {
      const newImage = this.product.images[this.currentIndex];
      const swalImage = document.getElementById('swal-image');

      // Update the modal image
      if (swalImage) {
        swalImage.src = newImage.url;
      }

      // Also update the main image in the component
      this.selectedImage = newImage;
    },

    resetSlide() {
      // Reset the slide direction class after the animation ends
      this.slideDirection = '';
    },
  },
};
</script>

<style>
.main-image-container {
  position: relative;
  overflow: hidden;
  background: #fff;
  text-align: center;
  height: 20rem;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  transition: transform 0.5s ease-in-out; /* Smooth slide transition */
  border-radius:8px;
}

.slide-left {
  animation: slideLeft 0.5s forwards;
}

.slide-right {
  animation: slideRight 0.5s forwards;
}

@keyframes slideLeft {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideRight {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.thumbnail {
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
  border-radius:5px;
}

.main-image:hover {
  cursor: pointer;
}

.thumbnail-container {
  width: 72px;
  height: 72px;
  padding:5px;
  cursor: pointer;
  background:#fff;
  float:left;
  margin-right:10px;
  margin-top:10px;
  text-align: center;
  border-radius:5px;
}

.last-container {
  margin-right: 0;
}

.thumbnail-container.active {
  border: 1px solid #d9d9d9;
}

.swal-image-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  border: none !important;
  outline: none !important;
}

.swal-image {
  max-height: 80%;
  object-fit: contain;
  border-radius:8px;
}

.prev-button, .next-button {
  position: absolute;
  top: 50%;
  background: none;
  color: #919191;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 10px;
  transform: translateY(-50%);
}

.prev-button:hover, .next-button:hover {
  color: #252525;
}

.prev-button {
  left: 10px;
}

.next-button {
  right: 10px;
}
</style>
